// src/components/PortalMonitorView.tsx
import React, { useEffect, useState } from 'react';
import { resolveStructureIdFromSlug } from '../firebase/firestore';
import { auth } from '../firebase/firebase';
import { Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Building2, CheckCircle, AlertTriangle, ArrowLeft, User, Monitor
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';

interface PortalMonitorViewProps {
  onBack: () => void;
  portalMonitor?: { structureData: any; portalName: string } | null;
}

type PortalMonitorDto = {
  structureName?: string | null;
  name?: string | null;
  type?: string | null;
  structureType?: string | null;
  location?: string | null;
  locality?: string | null;
  complianceByCategory?: Record<string, number | null> | null;
  compliance_by_category?: Record<string, number | null> | null;
  scores?: Record<string, number | null> | null;
};

type NormalizedPortalMonitor = {
  structureName: string | null;
  type: string | null;
  location: string | null;
  complianceByCategory: Record<string, number | null>;
};

async function fetchPortalMonitorDto(
  structureId: string,
  portalName: string
): Promise<{ ok: boolean; status: number; dto: PortalMonitorDto | null }> {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    return { ok: false, status: 401, dto: null };
  }

  const encodedPortal = encodeURIComponent(portalName || '');
  const res = await fetch(`/nextapi/ro/portal/structures/${encodeURIComponent(structureId)}/monitor?portal=${encodedPortal}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  let payload: PortalMonitorDto | null = null;
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      payload = (await res.json()) as PortalMonitorDto;
    } catch {
      payload = null;
    }
  }

  return { ok: res.ok, status: res.status, dto: payload };
}

function normalizePortalMonitor(
  dto: PortalMonitorDto | null,
  selectedStructureObject: any
): NormalizedPortalMonitor {
  const normalizeText = (val: any): string | null => {
    if (val === null || val === undefined) return null;
    const text = String(val).trim();
    return text ? text : null;
  };

  const labelFromSelected =
    normalizeText(selectedStructureObject?._meta?.name) ||
    normalizeText(selectedStructureObject?.structure_name) ||
    normalizeText(selectedStructureObject?.structureName) ||
    normalizeText(selectedStructureObject?.name) ||
    normalizeText(selectedStructureObject?.title) ||
    normalizeText(selectedStructureObject?.structure?.accommodation_data?.accommodation_name) ||
    normalizeText(selectedStructureObject?.accommodation_name) ||
    null;

  const typeFromSelected =
    normalizeText(selectedStructureObject?._meta?.type) ||
    normalizeText(selectedStructureObject?.structure_type) ||
    normalizeText(selectedStructureObject?.structureType) ||
    normalizeText(selectedStructureObject?.type) ||
    normalizeText(selectedStructureObject?.structure?.type) ||
    normalizeText(selectedStructureObject?.structure?.accommodation_data?.type) ||
    null;

  const locationFromSelected =
    normalizeText(selectedStructureObject?._meta?.locality) ||
    normalizeText(selectedStructureObject?.locality) ||
    normalizeText(selectedStructureObject?.location) ||
    normalizeText(selectedStructureObject?.city) ||
    normalizeText(selectedStructureObject?.address?.city) ||
    normalizeText(selectedStructureObject?.address?.province) ||
    normalizeText(selectedStructureObject?.structure?.accommodation_data?.address?.city) ||
    normalizeText(selectedStructureObject?.structure?.accommodation_data?.address?.province) ||
    (typeof selectedStructureObject?.address === 'string' ? normalizeText(selectedStructureObject?.address) : null) ||
    null;

  const dtoName = normalizeText(dto?.structureName) || normalizeText(dto?.name) || null;
  const dtoType = normalizeText(dto?.type) || normalizeText(dto?.structureType) || null;
  const dtoLocation = normalizeText(dto?.location) || normalizeText(dto?.locality) || null;

  const rawScores =
    dto?.complianceByCategory ||
    dto?.compliance_by_category ||
    dto?.scores ||
    {};

  const complianceByCategory: Record<string, number | null> = {};
  Object.entries(rawScores || {}).forEach(([key, value]) => {
    if (typeof value === 'number' && !Number.isNaN(value)) {
      const normalizedValue = value <= 1 ? value * 100 : value;
      complianceByCategory[key] = normalizedValue;
    } else {
      complianceByCategory[key] = null;
    }
  });

  return {
    structureName: dtoName || labelFromSelected,
    type: dtoType || typeFromSelected,
    location: dtoLocation || locationFromSelected,
    complianceByCategory,
  };
}

export function PortalMonitorView({ onBack, portalMonitor }: PortalMonitorViewProps) {
  const structureData = portalMonitor?.structureData;
  const portalName = portalMonitor?.portalName || 'Visit Bergamo';
  const [resolvedStructureId, setResolvedStructureId] = useState<string | null>(null);
  const [monitorError, setMonitorError] = useState<string | null>(null);
  const [monitorDto, setMonitorDto] = useState<PortalMonitorDto | null>(null);
  const [monitorStatus, setMonitorStatus] = useState<'idle' | 'loading' | 'loaded' | 'error' | 'empty'>('idle');

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!structureData) return;
      if (typeof structureData === 'string') {
        const resolvedId = resolveStructureIdFromSlug(structureData) || structureData;
        if (!mounted) return;
        setResolvedStructureId(String(resolvedId));
      } else {
        const resolvedId =
          structureData?.id_backend ||
          structureData?.backend_id ||
          structureData?.structureId ||
          structureData?.id ||
          structureData?.structure?.id ||
          '';
        if (!mounted) return;
        setResolvedStructureId(String(resolvedId) || null);
      }
    }
    load();
    return () => { mounted = false; };
  }, [structureData]);

  const currentStructure = typeof structureData === 'object' ? structureData : null;

  // Handle both data formats - from Portal Manager Dashboard and mock data
  const extractValue = (val: any) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'object' && val.value !== undefined) {
      return String(val.value);
    }
    return String(val);
  };

  const getStructureLabel = (structure: any): string | null => {
    if (!structure) return null;
    if (typeof structure === 'string') return structure;

    const directName =
      structure?._meta?.name ||
      structure?.name ||
      structure?.structure?.accommodation_data?.accommodation_name ||
      structure?.accommodation_name ||
      null;
    if (directName) return String(directName);

    // PRIORITIZE "NOME STRUTTURA" (6.1) from "Dati info azienda" module
    if (Array.isArray(structure?.modules) && structure.modules.length > 0) {
      const firstModule = structure.modules[0];
      if (firstModule?.content) {
        let nomeStruttura = extractValue(firstModule.content["6.1"]);
        if (!nomeStruttura) {
          nomeStruttura = extractValue(firstModule.content["NOME STRUTTURA"]);
        }
        if (nomeStruttura && nomeStruttura.trim()) {
          return nomeStruttura;
        }
      }
    }

    return null;
  };

  const getStructureLocation = (structure: any): string | null => {
    if (!structure || typeof structure === 'string') return null;
    const directLocation =
      structure?._meta?.locality ||
      structure?.locality ||
      structure?.structure?.accommodation_data?.address?.city ||
      structure?.structure?.accommodation_data?.address?.province ||
      structure?.address?.city ||
      structure?.address?.province ||
      null;
    if (directLocation) return String(directLocation);
    if (typeof structure?.address === 'string') return structure.address;
    return null;
  };

  const structureId = resolvedStructureId || (currentStructure as any)?.id || null;
  const preferredStructure = typeof structureData === 'object' ? structureData : currentStructure;
  const normalizedMonitor = normalizePortalMonitor(monitorDto, preferredStructure);
  const structureName = normalizedMonitor.structureName || getStructureLabel(preferredStructure) || getStructureLabel(currentStructure) || "Unknown Structure";
  const structureType =
    normalizedMonitor.type ||
    (typeof structureData === 'object'
      ? structureData?.type ||
        structureData?.structure?.accommodation_data?.type ||
        structureData?.structure?.type ||
        structureData?._meta?.type
      : null) ||
    (currentStructure as any)?.type ||
    (currentStructure as any)?.structure?.accommodation_data?.type ||
    "Unknown Type";
  const structureLocation =
    normalizedMonitor.location ||
    getStructureLocation(preferredStructure) ||
    getStructureLocation(currentStructure) ||
    "Unknown Location";
  const visitPalette = {
    base: "#0f0f0f",
    border: "#1c1c1c",
    accent: "#ec2b3c",
  };

  useEffect(() => {
    let mounted = true;
    async function loadMonitor() {
      if (!structureId) return;
      setMonitorError(null);
      setMonitorStatus('loading');
      try {
        const result = await fetchPortalMonitorDto(structureId, portalName);
        if (!mounted) return;
        if (!result.ok) {
          if (result.status === 401 || result.status === 403) {
            setMonitorError('Non autorizzato per questa struttura/portal');
          } else if (result.status === 404) {
            setMonitorError('Struttura non trovata');
          } else {
            setMonitorError('Errore caricamento monitor');
          }
          setMonitorDto(null);
          setMonitorStatus('error');
          return;
        }

        const dto = result.dto;
        setMonitorDto(dto || null);
        const normalized = normalizePortalMonitor(dto, preferredStructure);
        const hasScores = Object.values(normalized.complianceByCategory || {}).some(
          (value) => typeof value === 'number' && !Number.isNaN(value)
        );
        setMonitorStatus(hasScores ? 'loaded' : 'empty');
      } catch {
        if (!mounted) return;
        setMonitorError('Errore caricamento monitor');
        setMonitorDto(null);
        setMonitorStatus('error');
      }
    }
    loadMonitor();
    return () => { mounted = false; };
  }, [structureId, portalName]);

  const categoryConfig = [
    { key: 'motoria', label: 'Motoria', color: '#16a34a' },
    { key: 'sensoriale', label: 'Sensoriale', color: '#ea580c' },
    { key: 'cognitiva', label: 'Cognitiva', color: '#eab308' },
    { key: 'alimentare', label: 'Alimentare', color: '#059669' },
  ];

  const complianceByCategory = normalizedMonitor.complianceByCategory || {};
  const currentAccessibilityData = categoryConfig.map((item) => {
    const rawScore = complianceByCategory[item.key];
    const score = typeof rawScore === 'number' && !Number.isNaN(rawScore) ? rawScore : null;
    return {
      category: item.label,
      score,
      scoreValue: score ?? 0,
      color: item.color,
      requirements: { completed: 0, missing: 0 },
    };
  });

  const urgentImprovementCategories = currentAccessibilityData.filter(
    (item) => typeof item.score === 'number' && item.score < 75
  );

  const formatScore = (score: number | null) => (score === null ? 'N/D' : `${Math.round(score)}%`);

  const getScoreColor = (score: number | null): string => {
    if (score === null) return '#94a3b8';
    if (score >= 75) return '#22c55e';
    if (score >= 50) return '#eab308';
    return '#ef4444';
  };

  const getScoreTextClass = (score: number | null): string => {
    if (score === null) return 'text-slate-500';
    if (score >= 75) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreSurfaceClass = (score: number | null): string => {
    if (score === null) return 'border-slate-200 bg-slate-50/50';
    if (score >= 75) return 'border-green-200 bg-green-50/50';
    if (score >= 50) return 'border-yellow-200 bg-yellow-50/50';
    return 'border-red-200 bg-red-50/50';
  };

  const getScoreIconSurfaceClass = (score: number | null): string => {
    if (score === null) return 'bg-slate-100';
    if (score >= 75) return 'bg-green-100';
    if (score >= 50) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const getScoreIconClass = (score: number | null): string => {
    if (score === null) return 'text-slate-600';
    if (score >= 75) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const isVisitBergamo = portalName.toLowerCase().includes("bergamo");

  return (
    <div className={isVisitBergamo ? "min-h-screen bg-[#f5f5f5]" : "min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50"}>
      {/* Header */}
      <header
        className={isVisitBergamo ? "p-4 shadow-lg border-b" : "bg-gradient-travelin border-b border-orange-200/30 p-4 shadow-lg"}
        style={isVisitBergamo ? { backgroundColor: visitPalette.base, borderColor: visitPalette.border } : undefined}
      >
        <div className="max-w-7xl mx-auto space-y-3">
          <div className="flex justify-center">
            <img
              src={isVisitBergamo ? "/visit-bergamo-logo.svg" : "/travelin-logo.svg"}
              alt={isVisitBergamo ? "Visit Bergamo" : "Travelin"}
              className="h-14 w-auto object-contain"
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="outline" 
                onClick={onBack}
                className={isVisitBergamo ? "border-white/20 text-white bg-white/10" : "bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white/20 hover:border-white"}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Torna alla dashboard portale
              </Button>
              <div className="text-white">
                <h1 className="text-l font-medium">Monitor accessibilità</h1>
                <p className={`text-s ${isVisitBergamo ? "text-white" : "text-orange-100"}`}>{structureName}</p>
                <div className="mt-1 flex items-center gap-2 text-ss text-white/80">
                  <span>Offerto da</span>
                  <img
                    src="/travelin-logo.svg"
                    alt="Travelin"
                    className="h-4 w-auto rounded-sm bg-white px-1 py-0.5"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-s font-medium text-white">Gestore Portale</p>
                <p className={`text-ss ${isVisitBergamo ? "text-white" : "text-orange-100"}`}>{portalName}</p>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger className="h-10 w-10 rounded-full p-0 bg-transparent border-0 cursor-pointer hover:bg-white/10">
                  <div className={`w-8 h-8 rounded-full ${isVisitBergamo ? "bg-white/10 border border-white/15 text-white" : "bg-white text-travelin-blue"} flex items-center justify-center`}>
                    <User className="w-4 h-4" />
                  </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className={`w-56 ${isVisitBergamo ? "bg-[#0b0b0b] text-white border border-[#1c1c1c]" : ""}`}
                  style={isVisitBergamo ? { backgroundColor: "#0b0b0b", borderColor: "#1c1c1c" } : undefined}
                >
                  <DropdownMenuLabel className="pb-2">
                    <div className="flex flex-col space-y-1">
                      <p className="text-s font-medium">Gestore Portale</p>
                      <p className={`text-ss ${isVisitBergamo ? "text-white/70" : "text-muted-foreground"}`}>{portalName}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  
                  <DropdownMenuItem onClick={onBack} className="cursor-pointer">
                    <ArrowLeft className="w-4 h-4 mr-3" />
                    <span>Torna alla dashboard portale</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {monitorStatus === 'loading' && (
            <Card className="border-slate-200 bg-white">
              <CardHeader>
                <CardTitle>Caricamento monitor...</CardTitle>
                <CardDescription>
                  Stiamo recuperando i dati di conformita per questa struttura.
                </CardDescription>
              </CardHeader>
            </Card>
          )}

          {monitorStatus === 'error' && monitorError && (
            <Card className="border-red-200 bg-red-50">
              <CardHeader>
                <CardTitle className="text-red-700">{monitorError}</CardTitle>
                <CardDescription className="text-red-700">
                  Verifica i permessi o la configurazione del portale.
                </CardDescription>
              </CardHeader>
            </Card>
          )}

          {monitorStatus === 'empty' && (
            <Card className="border-amber-200 bg-amber-50">
              <CardHeader>
                <CardTitle className="text-amber-700">Nessun dato di monitor disponibile</CardTitle>
                <CardDescription className="text-amber-700">
                  Il backend non ha restituito dati di conformita per questa struttura.
                </CardDescription>
              </CardHeader>
            </Card>
          )}

          {/* Structure Information Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-gradient-travelin flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">{structureName}</CardTitle>
                    <CardDescription className="text-m">
                      {structureType}
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-slate-50 border-slate-200 text-slate-700">
                    <Monitor className="w-3 h-3 mr-1" />
                    Vista monitoraggio
                  </Badge>
                  <Badge variant="outline" className="bg-orange-50 border-orange-200 text-orange-700">
                    Accesso gestore portale
                  </Badge>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Accessibility Compliance Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Conformità accessibilità per categoria</CardTitle>
                <CardDescription>Percentuale di conformità nelle diverse aree di accessibilità</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={currentAccessibilityData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="category" 
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis />
                    <Tooltip
                      formatter={(value: any, _name: any, entry: any) => [
                        entry?.payload?.score === null ? 'N/D' : `${Math.round(value)}%`,
                        'Punteggio',
                      ]}
                    />
                    <Bar dataKey="scoreValue">
                      {currentAccessibilityData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={getScoreColor(entry.score)} 
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Attenzione richiesta</CardTitle>
                <CardDescription>Categorie che richiedono attenzione (sotto il 75% di conformità)</CardDescription>
              </CardHeader>
              <CardContent>
                {urgentImprovementCategories.length > 0 ? (
                  <div className="space-y-4">
                    {urgentImprovementCategories.map((item, index) => {
                      const total = item.requirements.completed + item.requirements.missing;
                      const percentage = total > 0 ? Math.round((item.requirements.completed / total) * 100) : 0;
                      
                      return (
                        <div key={index} className={`flex items-center justify-between p-4 border rounded-lg ${getScoreSurfaceClass(item.score)}`}>
                          <div className="flex items-center gap-4">
                            <div className={`flex items-center justify-center w-12 h-12 rounded-lg ${getScoreIconSurfaceClass(item.score)}`}>
                              <AlertTriangle className={`w-6 h-6 ${getScoreIconClass(item.score)}`} />
                            </div>
                            <div>
                              <h4 className="font-medium text-foreground">{item.category}</h4>
                              <div className="flex items-center gap-4 text-s text-muted-foreground mt-1">
                                <span>Punteggio: <span className={`${getScoreTextClass(item.score)} font-medium`}>{formatScore(item.score)}</span></span>
                                <span>•</span>
                                <span>Mancanti: <span className="font-medium">{item.requirements.missing} requisiti</span></span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="w-16 h-2 bg-gray-200 rounded-full mb-2">
                              <div 
                                className="h-2 rounded-full"
                                style={{ width: `${percentage}%`, backgroundColor: getScoreColor(item.score) }}
                              ></div>
                            </div>
                            <p className="text-ss text-muted-foreground">{percentage}% completato</p>
                          </div>
                        </div>
                      );
                    })}
                    <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                        <div>
                          <h4 className="text-s font-medium text-yellow-800">Gestore struttura avvisato</h4>
                          <p className="text-s text-yellow-700 mt-1">
                            Queste categorie richiedono attenzione da parte del gestore struttura per migliorare la conformità complessiva all'accessibilità.
                            Il gestore struttura è stato notificato automaticamente di queste aree prioritarie.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-l mb-2">Conformità eccellente</h3>
                    <p className="text-muted-foreground">
                      Tutte le categorie di accessibilità superano il 75% di conformità.
                      Nessun miglioramento urgente richiesto al momento.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Requirements Summary */}
          <Card style={{ opacity: 0.2 }}>
            <CardHeader>
              <CardTitle>Riepilogo requisiti</CardTitle>
              <CardDescription>Panoramica requisiti completati vs mancanti per categoria</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                {currentAccessibilityData.map((item, index) => {
                  const total = item.requirements.completed + item.requirements.missing;
                  const percentage = total > 0 ? Math.round((item.requirements.completed / total) * 100) : 0;
                  
                  return (
                    <div key={index} className="text-center p-4 border rounded-lg">
                      <h4 className="mb-3">{item.category}</h4>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-s">
                          <span className="text-green-600">Completati:</span>
                          <span className="font-medium">{item.requirements.completed}</span>
                        </div>
                        <div className="flex items-center justify-between text-s">
                          <span className="text-red-600">Mancanti:</span>
                          <span className="font-medium">{item.requirements.missing}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                          <div 
                            className="h-2 rounded-full"
                            style={{ width: `${percentage}%`, backgroundColor: getScoreColor(item.score) }}
                          ></div>
                        </div>
                        <p className="text-ss text-muted-foreground mt-2">
                          {percentage}% completato
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Overview Statistics */}
         {/*  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-s text-muted-foreground">Overall Compliance</p>
                    <p className="text-xxl">
                      {Math.round(currentAccessibilityData.reduce((sum, item) => sum + item.score, 0) / currentAccessibilityData.length)}%
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-s text-muted-foreground">Categories Above 75%</p>
                    <p className="text-xxl">{currentAccessibilityData.filter(item => item.score >= 75).length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-s text-muted-foreground">Needs Attention</p>
                    <p className="text-xxl">{urgentImprovementCategories.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div> */}

          {/* Information Card */}
          <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-orange-200">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center flex-shrink-0">
                  <Monitor className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-orange-900 mb-2">Vista monitoraggio gestore portale</h3>
                  <p className="text-orange-800 text-s leading-relaxed">
                    Questa dashboard di monitoraggio fornisce accesso in sola lettura ai dati di conformità sull'accessibilità delle strutture del portale.
                    Il gestore struttura riceve notifiche automatiche per le categorie che richiedono attenzione ed è responsabile
                    dell'implementazione dei miglioramenti tramite i suoi strumenti dedicati.
                  </p>
                  <div className="mt-4 flex items-center gap-4 text-ss text-orange-700">
                    <span>• Monitoraggio conformità in tempo reale</span>
                    <span>• Notifiche automatiche ai gestori struttura</span>
                    <span>• Reportistica accessibilità completa</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
